import { useEffect, useState } from "react";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Shield, ShieldCheck, Smartphone, Mail, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";

/**
 * MfaSettingsCard — shown in Profile page.
 * - Lets email/password users enroll (TOTP or email) or change their MFA method.
 * - Hidden for Google OAuth users (Google handles 2FA on their side).
 */
const MfaSettingsCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("idle"); // idle | pick | enrol
  const [method, setMethod] = useState(null);
  const [secret, setSecret] = useState(null);
  const [qr, setQr] = useState(null);
  const [emailHint, setEmailHint] = useState(null);
  const [devOtp, setDevOtp] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mfa/status`, { credentials: "include" });
      if (res.ok) {
        const text = await res.text();
        try { setStatus(text ? JSON.parse(text) : null); } catch { setStatus(null); }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const startSetup = async (chosen) => {
    setMethod(chosen);
    setBusy(true);
    setCode("");
    try {
      const res = await fetch(`${API}/auth/mfa/setup/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ method: chosen }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Could not start MFA setup");
      if (chosen === "totp") {
        setSecret(data.secret); setQr(data.qr);
      } else {
        setEmailHint(data.email_hint); setDevOtp(data.dev_otp || null);
      }
      setStage("enrol");
    } catch (err) {
      toast.error(err.message);
      setStage("pick");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/mfa/setup/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ method, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Invalid code");
      toast.success(`MFA enabled (${method === "totp" ? "Authenticator app" : "Email code"})`);
      setStage("idle");
      setSecret(null); setQr(null); setCode(""); setDevOtp(null);
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (!status) return null;
  if (status.google_oauth_user) {
    return (
      <Card className="card-surface" data-testid="mfa-google-card">
        <CardHeader><CardTitle className="text-slate-900 text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Two-factor authentication</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600">You signed in with Google. Manage your two-step verification directly in your <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] hover:underline">Google account security settings</a>.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-surface" data-testid="mfa-settings-card">
      <CardHeader>
        <CardTitle className="text-slate-900 text-lg flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#FFD507]" /> Two-factor authentication
          {status.enabled && <span className="text-xs font-normal bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded ml-2">ON</span>}
          {!status.enabled && status.mandatory && <span className="text-xs font-normal bg-amber-50 text-amber-800 px-2 py-0.5 rounded ml-2">Required for your role</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.enabled ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="mfa-enabled-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-emerald-900">MFA is active</p>
              <p className="text-sm text-emerald-800 mt-0.5">
                Method: <span className="font-medium">{status.method === "totp" ? "Authenticator app" : "Email one-time code"}</span>
              </p>
              <p className="text-xs text-emerald-800/80 mt-2">To change methods, ask an admin to reset MFA on your account, then enroll again.</p>
            </div>
          </div>
        ) : (
          <>
            {status.encouraged && (
              <p className="text-sm text-slate-600">
                Add an extra layer of security to your Aspiritech Academy account. It only takes a minute.
              </p>
            )}
            {stage === "idle" && (
              <Button onClick={() => setStage("pick")} className="btn-primary" data-testid="mfa-enable-btn">
                <Shield className="w-4 h-4 mr-2" /> Enable MFA
              </Button>
            )}
            {stage === "pick" && (
              <div className="space-y-2" data-testid="mfa-settings-pick">
                <button onClick={() => startSetup("totp")} disabled={busy} className="w-full border border-slate-200 rounded-lg p-3 hover:border-[#1E3A8A] hover:bg-slate-50 text-left flex items-start gap-3" data-testid="mfa-settings-pick-totp">
                  <Smartphone className="w-5 h-5 text-[#1E3A8A] mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 text-sm">Authenticator app <span className="text-xs text-emerald-700 ml-1">Recommended</span></p>
                    <p className="text-xs text-slate-500">Google Authenticator, Authy, 1Password, etc.</p>
                  </div>
                </button>
                <button onClick={() => startSetup("email")} disabled={busy} className="w-full border border-slate-200 rounded-lg p-3 hover:border-[#1E3A8A] hover:bg-slate-50 text-left flex items-start gap-3" data-testid="mfa-settings-pick-email">
                  <Mail className="w-5 h-5 text-[#1E3A8A] mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 text-sm">Email one-time code</p>
                    <p className="text-xs text-slate-500">6-digit code emailed each time you sign in.</p>
                  </div>
                </button>
                <button onClick={() => setStage("idle")} className="text-xs text-slate-500 hover:text-slate-700 mt-1">Cancel</button>
              </div>
            )}
            {stage === "enrol" && method === "totp" && (
              <div className="space-y-3" data-testid="mfa-settings-totp">
                <div className="flex flex-col items-center bg-slate-50 rounded-lg p-3">
                  {qr && <img src={qr} alt="Scan with authenticator app" className="w-40 h-40" />}
                  <p className="text-xs text-slate-500 mt-2">Scan with your authenticator app.</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Manual secret</p>
                  <div className="font-mono text-xs text-slate-900 break-all select-all">{secret}</div>
                </div>
                <form onSubmit={confirm} className="space-y-2">
                  <Label className="text-slate-600 text-sm">Code from app</Label>
                  <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="••••••" className="input-light text-center text-xl tracking-[0.4em] font-mono h-12" data-testid="mfa-settings-totp-code" autoComplete="one-time-code" />
                  <div className="flex gap-2">
                    <Button type="submit" disabled={busy || code.length !== 6} className="btn-primary flex-1" data-testid="mfa-settings-totp-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}</Button>
                    <Button type="button" variant="outline" onClick={() => { setStage("pick"); setCode(""); }}>Back</Button>
                  </div>
                </form>
              </div>
            )}
            {stage === "enrol" && method === "email" && (
              <div className="space-y-3" data-testid="mfa-settings-email">
                <p className="text-sm text-slate-700">We sent a 6-digit code to <span className="font-medium">{emailHint}</span>.</p>
                {devOtp && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded p-2 text-center" data-testid="dev-otp-settings">
                    <span className="font-bold">DEV / PREVIEW</span> — code is <span className="font-mono font-bold">{devOtp}</span>
                  </div>
                )}
                <form onSubmit={confirm} className="space-y-2">
                  <Label className="text-slate-600 text-sm">Verification code</Label>
                  <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="••••••" className="input-light text-center text-xl tracking-[0.4em] font-mono h-12" data-testid="mfa-settings-email-code" autoComplete="one-time-code" />
                  <div className="flex gap-2">
                    <Button type="submit" disabled={busy || code.length !== 6} className="btn-primary flex-1" data-testid="mfa-settings-email-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}</Button>
                    <Button type="button" variant="outline" onClick={() => { setStage("pick"); setCode(""); }}>Back</Button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MfaSettingsCard;


/**
 * MfaEncourageBanner — dismissible banner shown in dashboard for mentors/participants
 * who haven't enrolled in MFA. Hidden for Google users and users who already dismissed.
 */
export const MfaEncourageBanner = () => {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    fetch(`${API}/auth/mfa/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStatus)
      .catch(() => {});
  }, []);

  if (hidden || !status) return null;
  if (status.google_oauth_user) return null;
  if (status.enabled) return null;
  if (!status.encouraged) return null;
  if (status.mfa_dismissed_at) return null;

  const dismiss = async () => {
    setHidden(true);
    try { await fetch(`${API}/auth/mfa/dismiss-banner`, { method: "POST", credentials: "include" }); } catch (_) {}
  };

  return (
    <div className="bg-gradient-to-r from-[#FFD507]/15 to-[#1E3A8A]/5 border border-[#FFD507]/40 rounded-xl p-4 flex items-center gap-4" data-testid="mfa-encourage-banner">
      <div className="w-10 h-10 rounded-full bg-[#FFD507]/30 flex items-center justify-center flex-shrink-0">
        <Shield className="w-5 h-5 text-[#1E3A8A]" />
      </div>
      <div className="flex-1">
        <p className="font-semibold text-slate-900 text-sm">Protect your account with two-factor authentication</p>
        <p className="text-xs text-slate-600 mt-0.5">Takes a minute. Adds a strong second step so only you can sign in.</p>
      </div>
      <div className="flex items-center gap-2">
        <a href="/profile" className="text-sm font-semibold text-[#1E3A8A] hover:underline" data-testid="mfa-banner-cta">Enable MFA →</a>
        <button onClick={dismiss} className="text-xs text-slate-500 hover:text-slate-700" data-testid="mfa-banner-dismiss">Dismiss</button>
      </div>
    </div>
  );
};
